import type { Message } from '../../types';
import { getRepairDisplayStatus, isRepairStageDone } from '../repairDisplayState';
import type { StageTimelineItem } from '../stageTimelineAdapter';
import type { BuildTurnPresentationOptions } from './presentationTypes';

function translate(options: BuildTurnPresentationOptions, key: string, fallback: string): string {
  const value = options.translate?.(key);
  return value && value !== key ? value : fallback;
}

export function getActiveAuditItem(items: StageTimelineItem[]): StageTimelineItem | undefined {
  return items.find(item => item.status === 'needs_input')
    || items.find(item => item.status === 'needs_approval')
    || items.find(item => item.status === 'failed')
    || items.find(item => item.status === 'running');
}

/**
 * Derive a human-readable status text for the current working/recovery phase.
 * Returns undefined when no active stage needs attention.
 */
export function getStatusText(
  message: Message,
  items: StageTimelineItem[],
  options: BuildTurnPresentationOptions,
): string | undefined {
  const active = getActiveAuditItem(items);
  const kind = active?.kind;

  if (kind === 'requirementThinking' || kind === 'requirementAnalysis' || kind === 'requirementConfirmation') {
    return translate(options, 'Working requirement status', '正在确认控制目标和边界条件……');
  }
  if (kind === 'humanConfirmation') {
    return translate(options, 'Working clarification status', '需要你确认几个会影响程序的关键条件。');
  }
  if (kind === 'engineeringReasoning' || kind === 'planGeneration' || kind === 'planReview') {
    return translate(options, 'Working plan status', '正在组织控制逻辑和程序结构……');
  }
  if (kind === 'code') {
    return translate(options, 'Working code status', '正在生成 PLC 程序……');
  }
  if (kind === 'validation') {
    return translate(options, 'Working validation status', '正在检查语法、I/O 和安全联锁……');
  }
  if (kind === 'repair' || getRepairDisplayStatus(message) === 'running') {
    return translate(options, 'Working repair status', '正在修复检查中发现的问题……');
  }
  // TA10b: the repair stage can finish before `repairAppendix.status` and
  // `progressState.phase` catch up, so once it is genuinely done the status
  // line must say so instead of falling through to the generic "processing"
  // line — see `isRepairStageDone`.
  if (isRepairStageDone(message) && message.isStreaming) {
    return translate(options, 'Working final checks status', '最终校验中');
  }
  if (message.isStreaming) {
    return translate(options, 'Working status', '正在处理你的需求……');
  }
  return undefined;
}
